import * as React from 'react';

import { cn } from '@/lib/utils';

const Textarea = React.forwardRef<
  HTMLTextAreaElement,
  React.TextareaHTMLAttributes<HTMLTextAreaElement> & {
    autoResize?: boolean;
    withBorder?: boolean;
  }
>(({ className, autoResize = true, withBorder = true, ...props }, ref) => {
  const textareaRef = React.useRef<HTMLTextAreaElement>(null);

  const handleInput = React.useCallback(
    (textarea: HTMLTextAreaElement) => {
      if (!autoResize) return;
      textarea.style.height = 'auto'; // Reset height
      textarea.style.height = `${textarea.scrollHeight}px`; // Set to scrollHeight
    },
    [autoResize]
  );

  React.useEffect(() => {
    if (textareaRef.current) {
      handleInput(textareaRef.current);
    }
  }, [handleInput]);

  return (
    <textarea
      className={cn(
        'bg-white-300 placeholder:text-muted-foreground focus-visible:ring-ring flex w-full resize-none rounded-lg px-6 py-[14.5px] text-sm leading-5 shadow-xs focus-visible:outline-hidden disabled:cursor-not-allowed disabled:opacity-50',
        autoResize && 'overflow-hidden',
        withBorder && 'border-white-600 bg-white-400 border',
        className
      )}
      ref={(element) => {
        // Handle both refs
        if (typeof ref === 'function') ref(element);
        else if (ref) ref.current = element;
        textareaRef.current = element;
      }}
      rows={1}
      onInput={(e) => handleInput(e.currentTarget)}
      {...props}
    />
  );
});
Textarea.displayName = 'Textarea';

export { Textarea };
